/**
 * Implementation of the per-guild raid-list fetch. Pulled out of
 * `actions.ts` so that file can stay focused on the user-action surface
 * (create / schedule / delete / apply). The public
 * `loadRaids` symbol still lives on `actions.ts` — it imports the
 * implementation from here and re-exports it through `RaidActionsApi`.
 *
 * `parseRaidSummaryEvent` is module-local: it never escapes this file,
 * only `useLoadRaids(ctx)` does. `snowflakeVar` is the same identity
 * function the calendar's `loadRaidsForWindow` uses — kept locally so the
 * file stands alone if its caller is the only consumer.
 */
import type { Ref } from 'vue'

import { resolveClient } from '@/shared/ports/resolveClient'
import { RaidsDocument } from '../../lib/handWrittenGraphql'
import type { RaidsQuery } from '../../lib/handWrittenGraphql'
import type { RaidSummary } from '../../types'

// Local alias — see `stores/board/actions.ts` for the rationale.
const apolloClient = resolveClient()

function snowflakeVar(s: string): string {
  return s
}

function parseRaidSummaryEvent(
  raidId: number,
  payload: string | null,
): RaidSummary | null {
  if (payload === null || payload === '') {
    return null
  }
  try {
    const raw = JSON.parse(payload) as Record<string, unknown>
    const recurrenceSeriesId = typeof raw['recurrenceSeriesId'] === 'number'
      ? (raw['recurrenceSeriesId'] as number)
      : null
    const recurrenceIndex = typeof raw['recurrenceIndex'] === 'number'
      ? (raw['recurrenceIndex'] as number)
      : 0
    const recurrence = recurrenceSeriesId === null
      ? null
      : {
          seriesId: String(recurrenceSeriesId),
          parentId: String(recurrenceSeriesId),
          index: recurrenceIndex,
          total: 1,
          frequency: 'weekly' as const,
        }
    return {
      id: String(raidId),
      guildId: String(typeof raw['guildId'] === 'number' ? raw['guildId'] : 0),
      name: typeof raw['name'] === 'string' ? (raw['name'] as string) : '',
      isPublished: raw['isPublished'] === true,
      scheduledStartsAt: typeof raw['scheduledStartsAt'] === 'string'
        ? (raw['scheduledStartsAt'] as string)
        : null,
      durationMinutes: typeof raw['durationMinutes'] === 'number'
        ? (raw['durationMinutes'] as number)
        : null,
      cancelledAt: typeof raw['cancelledAt'] === 'string'
        ? (raw['cancelledAt'] as string)
        : null,
      completedAt: typeof raw['completedAt'] === 'string'
        ? (raw['completedAt'] as string)
        : null,
      sourceRaidId: typeof raw['sourceRaidId'] === 'number'
        ? String(raw['sourceRaidId'] as number)
        : null,
      recurrence: recurrence as RaidSummary['recurrence'],
      notes: typeof raw['notes'] === 'string' ? (raw['notes'] as string) : '',
      createdBy: typeof raw['createdBy'] === 'number'
        ? String(raw['createdBy'] as number)
        : null,
      createdAt: typeof raw['createdAt'] === 'string'
        ? (raw['createdAt'] as string)
        : '',
      updatedAt: typeof raw['updatedAt'] === 'string'
        ? (raw['updatedAt'] as string)
        : '',
    }
  } catch {
    return null
  }
}

export interface LoadRaidsCtx {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly error: Ref<string | null>
  readonly raids: Ref<ReadonlyArray<RaidSummary>>
  readonly numericRaidId: Map<string, number>
}

/**
 * Fetch the per-guild raid-summary list. The result is sorted by
 * `scheduledStartsAt` ascending,
 * then by name. Mirrors the pre-extraction behaviour verbatim — the
 * snapshot test pins `loadRaids` as a public store key, so the
 * signature stays `(guildId: string) => Promise<RaidSummary[]>` even
 * though the slim composer wires the active guild from the store.
 */
export function useLoadRaids(ctx: LoadRaidsCtx): () => Promise<void> {
  let lastRequestedGuildId: string | null = null
  let requestGeneration = 0

  return async (): Promise<void> => {
    const guildId = ctx.activeDiscordGuildId.value
    const generation = ++requestGeneration
    if (guildId === '0') {
      lastRequestedGuildId = guildId
      ctx.raids.value = []
      ctx.numericRaidId.clear()
      return
    }
    if (lastRequestedGuildId !== guildId) {
      lastRequestedGuildId = guildId
      ctx.raids.value = []
      ctx.numericRaidId.clear()
    }
    try {
      const res = await apolloClient.query<RaidsQuery>({
        query: RaidsDocument,
        variables: { guildId: snowflakeVar(guildId) } as never,
        fetchPolicy: 'network-only',
      })
      if (
        generation !== requestGeneration
        || guildId !== ctx.activeDiscordGuildId.value
      ) return
      const events = res.data?.raidsForGuild ?? []
      const map = new Map<number, RaidSummary>()
      for (const e of events) {
        const summary = parseRaidSummaryEvent(e.raidId, e.payload)
        if (summary !== null) {
          map.set(e.raidId, summary)
        }
      }
      const list: RaidSummary[] = []
      map.forEach((s) => list.push(s))
      list.sort((a, b) => {
        const aKey = a.scheduledStartsAt ?? ''
        const bKey = b.scheduledStartsAt ?? ''
        if (aKey !== bKey) return aKey < bKey ? -1 : 1
        return a.name < b.name ? -1 : a.name > b.name ? 1 : 0
      })
      ctx.numericRaidId.clear()
      for (const s of list) ctx.numericRaidId.set(s.id, Number(s.id))
      ctx.raids.value = list
    } catch (e: unknown) {
      if (
        generation !== requestGeneration
        || guildId !== ctx.activeDiscordGuildId.value
      ) return
      ctx.error.value = e instanceof Error ? e.message : String(e)
    }
  }
}
